import React, { useContext, useMemo, useState, useEffect } from "react";
import { Modal } from "reactstrap";
import { IoClose } from "react-icons/io5";
import { FaCheckCircle, FaTimes } from "react-icons/fa";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import { Language } from "../../App";
import { _imagesURL, adminBaseURL } from "../../constants/API";
import PlaceholderImage from "../../assets/images/place.png";
import LockSubImage from "../../assets/images/LockSub.svg";
import BenefitsModal from "./BenefitsModal";
import MixpanelManager from "../../constants/MixpanelManager";
import { MixpanelEvents } from "../../constants/MixpanelEvents";

const formatDateAndTime = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'long' });
    const year = date.getFullYear();
    const time = date.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${day} ${month} ${year} ${time}`;
  } catch (error) {
    return dateString;
  }
};

export default function ModalSubscription({ isOpen, onClose, subscriptionData = {} }) {
  const { lang } = useContext(Language);
  const [imageLoading, setImageLoading] = useState(true);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [benefitsOpen, setBenefitsOpen] = useState(false);
  const [hasNoSubscription, setHasNoSubscription] = useState(false);
  const [showBenefitsModal, setShowBenefitsModal] = useState(false);

  const getUserDataFromStorage = () => {
    try {
      const signupData = localStorage.getItem("SignupData");
      return signupData ? JSON.parse(signupData).data : null;
    } catch (error) {
      console.error("Error parsing SignupData:", error);
      return null;
    }
  };

  const userData = useMemo(() => getUserDataFromStorage(), [isOpen]);

  useEffect(() => {
    setImageLoading(true);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    // Reset states on open
    setSubscriptionInfo(null);
    setHasNoSubscription(false);

    const fetchSubscription = async () => {
      setLoading(true);
      try {
        const signupData = JSON.parse(localStorage.getItem("SignupData"));
        const token = signupData?.data?.token;

        if (!token) {
          console.error("No token found");
          setLoading(false);
          setHasNoSubscription(true);
          return;
        }

        const response = await fetch(`${adminBaseURL}user/current/subscription`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          // If data is null → show upgrade screen
          if (data.data === null) {
            setHasNoSubscription(true);
            setSubscriptionInfo(null);
          } else {
            setSubscriptionInfo(data.data);
            setHasNoSubscription(false);
          }
        }
      } catch (error) {
        console.error("Error fetching subscription:", error);
        setHasNoSubscription(true);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [isOpen]);

  const formatBenefitKey = (key) => {
    const langKey = `benefit_${key}`;
    if (lang?.[langKey]) return lang[langKey];
    return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const fullImageUrl = userData?.image
    ? `${_imagesURL.trim()}${userData.image}`
    : null;

  const {
    recipientName = userData?.firstname || "User",
    recipientImage = fullImageUrl,
  } = subscriptionData;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');

        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        @keyframes msub-fadeIn {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .msub-shimmer {
          background: linear-gradient(to left, #f3f3f3 25%, #e0e0e0 37%, #f3f3f3 63%);
          background-size: 400% 100%;
          animation: shimmer 2s linear infinite;
          border-radius: 8px;
        }

        /* ── Modal wrapper ── */
        .msub-modal .modal-dialog {
          max-width: 420px;
          margin: auto;
          font-family: 'Outfit', sans-serif;
        }
        .msub-modal .modal-content {
          border: none;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 24px 70px rgba(0,0,0,0.13);
          background: #fff;
        }

        /* ── Header ── */
        .msub-header {
          background: #fff;
          padding: 20px 20px 0px;
          position: relative;
        }
        .msub-top-card {
          background: #F8F9FA;
          border: 1px solid #ECECEC;
          border-radius: 14px;
          padding: 18px 16px 16px;
          position: relative;
        }
        .msub-close-btn {
          position: absolute;
          top: 12px;
          right: 12px;
          width: 32px;
          height: 32px;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #555;
          font-size: 18px;
          transition: background 0.15s;
          z-index: 2;
          padding: 0;
          background: transparent;
        }

        .msub-recipient-row {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 16px;
        }
        .msub-avatar-wrap {
          width: 72px;
          height: 72px;
          border-radius: 16px;
          overflow: hidden;
          background: #e8f8fb;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          position: relative;
        }
        .msub-avatar-wrap img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .msub-recipient-label {
          font-size: 13px;
          font-weight: 600;
          color: #1DC1DD;
          margin: 0 0 2px;
        }
        .msub-recipient-name {
          font-size: 20px;
          font-weight: 500;
          color: #0f1419;
          margin: 0;
          letter-spacing: -0.01em;
        }

        /* ── Features card ── */
        .msub-features-card {
          background: transparent;
          padding: 14px 0 4px;
          margin-bottom: 0;
        }
        .msub-feature-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 6px 0;
        }
        .msub-feature-icon {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .msub-feature-text {
          font-size: 14px;
          font-weight: 500;
          color: #6b7280;
          margin: 0;
        }

        /* ── Details card ── */
        .msub-details-card {
          background: #F8F9FA;
          border: 1px solid #ECECEC;
          border-radius: 14px;
          padding: 14px 16px;
          margin-bottom: 14px;
        }
        .msub-detail-row {
          padding: 9px 0;
          border-bottom: 0.5px solid #e8eaee;
        }
        .msub-detail-row:last-child {
          border-bottom: none;
          padding-bottom: 2px;
        }
        .msub-detail-label {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.09em;
          text-transform: uppercase;
          color: #848798;
          margin: 0 0 2px;
        }
        .msub-detail-value {
          font-size: 14px;
          font-weight: 600;
          color: #111827;
          margin: 0;
        }

        /* ── Body ── */
        .msub-body {
          padding: 16px 20px 22px;
          background: #fff;
        }

        /* ── Full-modal shimmer overlay ── */
        .msub-full-loader {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.98);
          display: flex;
          flex-direction: column;
          gap: 0;
          z-index: 1050;
          border-radius: 20px;
          overflow-y: auto;
          padding: 20px;
          min-height: 700px;
        }
        .msub-modal.is-loading .modal-content {
          min-height: 700px;
        }

        /* ── Shimmer loader sections ── */
        .msub-shimmer-section-title {
          height: 20px;
          width: 180px;
          margin-bottom: 16px;
        }
        .msub-shimmer-top-card {
          background: #F8F9FA;
          border: 1px solid #ECECEC;
          border-radius: 14px;
          padding: 18px 16px 16px;
          margin-bottom: 16px;
        }
        .msub-shimmer-details-card {
          background: #F8F9FA;
          border: 1px solid #ECECEC;
          border-radius: 14px;
          padding: 14px 16px;
        }
        .msub-shimmer-detail-row {
          padding: 9px 0;
          border-bottom: 0.5px solid #e8eaee;
        }
        .msub-shimmer-detail-row:last-child {
          border-bottom: none;
        }

        /* ── Upgrade Screen ── */
        .msub-upgrade-wrap {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 52px 32px 44px;
          text-align: center;
          font-family: 'Outfit', sans-serif;
          animation: msub-fadeIn 0.35s ease both;
        }
        .msub-upgrade-icon-ring {
         
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 26px;
        }
        .msub-upgrade-title {
          font-size: 22px;
          font-weight: 700;
          color: #0f1419;
          margin: 0 0 10px;
          letter-spacing: -0.01em;
        }
        .msub-upgrade-desc {
          font-size: 14px;
          color: #6b7280;
          font-weight: 400;
          margin: 0 0 34px;
          line-height: 1.65;
          max-width: 470px;
        }
        .msub-upgrade-btn {
          width: 100%;
          padding: 15px 0;
          background: #1DC1DD;
          border: none;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 700;
          color: #fff;
          cursor: pointer;
          letter-spacing: 0.02em;
          font-family: 'Outfit', sans-serif;
          transition: opacity 0.2s, transform 0.15s;
        }
        .msub-upgrade-btn:hover {
          opacity: 0.88;
          transform: translateY(-1px);
        }
        .msub-upgrade-btn:active {
          transform: translateY(0);
          opacity: 1;
        }

        @media (max-width: 480px) {
          .msub-modal .modal-dialog { padding: 0 10px; margin: 10px auto; }
          .msub-header { padding: 26px 16px 20px; }
          .msub-avatar-wrap { width: 60px; height: 60px; }
          .msub-recipient-name { font-size: 16px; }
          .msub-body { padding: 6px 14px 18px; }
          .msub-feature-text { font-size: 12px; }
          .msub-detail-value { font-size: 13px; }
          .msub-upgrade-wrap { padding: 40px 20px 36px; }
          .msub-upgrade-title { font-size: 19px; }
        }
      `}</style>

      <Modal
        isOpen={isOpen}
        toggle={onClose}
        centered
        backdrop="static"
        className={`msub-modal${loading ? " is-loading" : ""}`}
      >
        {/* ── SHIMMER LOADER ── */}
        {loading && (
          <div className="msub-full-loader">
            <div style={{ position: "relative", marginBottom: "0" }}>
              <div className="msub-shimmer msub-shimmer-section-title" />
              <div className="msub-shimmer-top-card">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                    marginBottom: "20px",
                  }}
                >
                  <div
                    className="msub-shimmer"
                    style={{
                      width: "72px",
                      height: "72px",
                      borderRadius: "16px",
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div
                      className="msub-shimmer"
                      style={{ height: "14px", width: "60px", marginBottom: "8px" }}
                    />
                    <div
                      className="msub-shimmer"
                      style={{ height: "20px", width: "130px" }}
                    />
                  </div>
                </div>
                <div style={{ borderTop: "1px solid #eceef2", paddingTop: "14px" }}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "6px 0",
                      }}
                    >
                      <div
                        className="msub-shimmer"
                        style={{
                          width: "20px",
                          height: "20px",
                          borderRadius: "50%",
                          flexShrink: 0,
                        }}
                      />
                      <div
                        className="msub-shimmer"
                        style={{
                          flex: 1,
                          height: "14px",
                          width: `${60 + (i % 3) * 15}%`,
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ padding: "16px 0 0" }}>
              <div className="msub-shimmer-details-card">
                {[
                  { labelW: "120px", valueW: "180px" },
                  { labelW: "100px", valueW: "110px" },
                  { labelW: "60px", valueW: "70px" },
                  { labelW: "110px", valueW: "150px" },
                  { labelW: "90px", valueW: "150px" },
                ].map((row, i) => (
                  <div key={i} className="msub-shimmer-detail-row">
                    <div
                      className="msub-shimmer"
                      style={{ height: "10px", width: row.labelW, marginBottom: "6px" }}
                    />
                    <div
                      className="msub-shimmer"
                      style={{ height: "14px", width: row.valueW }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── UPGRADE SCREEN — jab data null ho ── */}
        {!loading && hasNoSubscription && (
          <div style={{ position: "relative" }}>
            {/* Close button */}
            <button
              className="msub-close-btn"
              onClick={onClose}
              aria-label="Close"
              style={{ position: "absolute", top: "14px", right: "14px" }}
            >
              <IoClose />
            </button>

            <div className="msub-upgrade-wrap">
              {/* Lock icon */}
              <div className="msub-upgrade-icon-ring">
               <img   src={LockSubImage} alt="" />
              </div>

              {/* Title */}
              <h3 className="msub-upgrade-title">
                {lang?.msub_upgrade_title || "Upgrade to Premium"}
              </h3>

              {/* Description */}
              <p className="msub-upgrade-desc">
                {lang?.msub_upgrade_desc ||
                  "An active subscription is required to access ophthalmology radar analysis and diagnostic features."}
              </p>

              {/* CTA Button */}
              <button
                className="msub-upgrade-btn"
                onClick={() => {
  setShowBenefitsModal(true);
  MixpanelManager.track(MixpanelEvents.VIEW_BENEFITS, {
    user_id: JSON.parse(localStorage.getItem("SignupData"))?.data?.id || "",
  });
}}
              >
                {lang?.msub_explore_benefits || "Explore Benefits"}
              </button>

             
              <a
                href="https://mail.google.com/mail/?view=cm&fs=1&to=support@ophthalmologyradar.com&su=Subscription%20Request%20%E2%80%93%20Ophthalmology%20Radar"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "block",
                  textAlign: "center",
                  marginTop: "12px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#1DC1DD",
                  textDecoration: "none",
                  cursor: "pointer",
                  transition: "opacity 0.2s",
                }}
                onMouseEnter={(e) => (e.target.style.opacity = "0.8")}
                onMouseLeave={(e) => (e.target.style.opacity = "1")}
              >
              <span  style={{
                 
                  marginTop: "12px",
                  fontSize: "14px",
                                  color: "black",
                  textDecoration: "none",
                
                }}>{lang?.help_sub} </span>  {lang?.msub_how_to_get_subscription}
              </a>
            </div>
          </div>
        )}

        {/* ── NORMAL SUBSCRIPTION VIEW ── */}
        {!loading && !hasNoSubscription && (
          <>
            {/* ── HEADER ── */}
            <div className="msub-header">
              <button
                className="msub-close-btn"
                onClick={onClose}
                aria-label="Close"
              >
                <IoClose />
              </button>

              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#0f1419",
                  marginBottom: "16px",
                  marginTop: "0",
                }}
              >
                {lang?.msub_current_subscription || "Current Subscription"}
              </h3>

              <div className="msub-top-card">
                <div className="msub-recipient-row">
                  <div className="msub-avatar-wrap">
                    {recipientImage ? (
                      <>
                        {imageLoading && (
                          <div
                            style={{
                              position: "absolute",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "100%",
                              height: "100%",
                              zIndex: 1,
                              background: "rgba(255,255,255,0.8)",
                            }}
                          >
                            <div
                              className="spinner-border"
                              style={{
                                width: "1.2rem",
                                height: "1.2rem",
                                color: "#1DC1DD",
                              }}
                              role="status"
                            >
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          </div>
                        )}
                        <img
                          src={recipientImage}
                          alt={recipientName}
                          onLoad={() => setImageLoading(false)}
                          onError={() => setImageLoading(false)}
                          style={{ display: imageLoading ? "none" : "block" }}
                        />
                      </>
                    ) : (
                      <img
                        src={PlaceholderImage}
                        alt="placeholder"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    )}
                  </div>
                  <div>
                    <p className="msub-recipient-name">{recipientName}</p>
                    <p className="msub-recipient-label">
                      {lang?.msub_recipient || "Recipient"}
                    </p>
                  </div>
                </div>

                {/* Benefits list */}
                <div className="msub-features-card">
                  {subscriptionInfo?.benefits && subscriptionInfo.benefits.length > 0 ? (
                    <>
                      {subscriptionInfo.benefits.slice(0, 5).map((benefit, index) => (
                        <div
                          key={benefit.id}
                          className="msub-feature-row"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "12px",
                            padding: "0px 0",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              flex: 1,
                            }}
                          >
                            <div className="msub-feature-icon">
                              {benefit.is_enabled ? (
                                <FaCheckCircle size={13} color="#00BC7D" />
                              ) : (
                                <FaTimes
                                  size={13}
                                  color="#FF2D55"
                                  style={{
                                    backgroundColor: "#FFE2E2",
                                    borderRadius: "50px",
                                    padding: "2px",
                                  }}
                                />
                              )}
                            </div>
                            <p className="msub-feature-text">
                              {lang?.[`benefit_${benefit.benefit_key}`] ||
                                formatBenefitKey(benefit.benefit_key)}
                            </p>
                          </div>
                          {index === 4 && subscriptionInfo.benefits.length > 5 && (
                            <div
                              onClick={() => setBenefitsOpen(!benefitsOpen)}
                              style={{
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "6px 12px",
                                border: "1px solid #dedddd",
                                borderRadius: "6px",
                                backgroundColor: "transparent",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  color: "#000",
                                }}
                              >
                                {benefitsOpen ? (lang?.see_less || "See Less") : (lang?.see_more || "See More")}
                              </span>
                              {benefitsOpen ? (
                                <MdKeyboardArrowUp size={16} color="#000" />
                                
                             
                              ) : (
                                <MdKeyboardArrowDown size={16} color="#000" />
                                
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                      {benefitsOpen && subscriptionInfo.benefits.length > 5 && (
                        <div
                          style={{ display: "flex", flexDirection: "column", gap: "0px" }}
                        >
                          {subscriptionInfo.benefits.slice(5).map((benefit) => (
                            <div
                              key={benefit.id}
                              style={{ display: "flex", alignItems: "center", gap: "12px" }}
                            >
                              <div className="msub-feature-icon">
                                {benefit.is_enabled ? (
                                  <FaCheckCircle size={13} color="#00BC7D" />
                                ) : (
                                  <FaTimes
                                    size={13}
                                    color="#FF2D55"
                                    style={{
                                      backgroundColor: "#FFE2E2",
                                      borderRadius: "50px",
                                      padding: "2px",
                                    }}
                                  />
                                )}
                              </div>
                              <p className="msub-feature-text">
                                {lang?.[`benefit_${benefit.benefit_key}`] ||
                                  formatBenefitKey(benefit.benefit_key)}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#848798",
                        textAlign: "center",
                        margin: 0,
                      }}
                    >
                      {lang?.msub_no_benefits || "No benefits available"}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* ── BODY ── */}
            <div className="msub-body">
              <div className="msub-details-card">
                <div className="msub-detail-row">
                  <p className="msub-detail-label">
                    {lang?.msub_account_email || "Account Email Address"}
                  </p>
                  <p className="msub-detail-value">
                    {userData?.email || userData?.user_name || "N/A"}
                  </p>
                </div>
                <div className="msub-detail-row">
                  <p className="msub-detail-label">
                    {lang?.msub_sub_type || "Subscription Type"}
                  </p>
                  <p className="msub-detail-value">
                    {subscriptionInfo?.plan_name || "N/A"}
                  </p>
                </div>
                <div className="msub-detail-row">
                  <p className="msub-detail-label">
                    {lang?.msub_current_status || "Status"}
                  </p>
                  <p
                    className="msub-detail-value"
                    style={{
                      color:
                        subscriptionInfo?.status === "active" ? "#1DC1DD" : "#FF2D55",
                    }}
                  >
                    {subscriptionInfo?.status
                      ? subscriptionInfo.status.charAt(0).toUpperCase() +
                        subscriptionInfo.status.slice(1)
                      : "N/A"}
                  </p>
                </div>
                <div className="msub-detail-row">
                  <p className="msub-detail-label">
                    {lang?.msub_purchasing_date || "Purchasing Date"}
                  </p>
                  <p className="msub-detail-value">
                    {formatDateAndTime(subscriptionInfo?.started_at)}
                  </p>
                </div>
                <div className="msub-detail-row">
                  <p className="msub-detail-label">
                    {lang?.msub_next_billing || "Expire Date"}
                  </p>
                  <p className="msub-detail-value">
                    {formatDateAndTime(subscriptionInfo?.ends_at)}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        <BenefitsModal
          isOpen={showBenefitsModal}
          onClose={() => setShowBenefitsModal(false)}
        />
      </Modal>
    </>
  );
}